import {Injectable} from '@angular/core';

export type BridgeMessage<T = unknown> = {
  type: string;
  payload?: T;
};

function safeStringify(obj: unknown): string {
  try {
    return JSON.stringify(obj);
  } catch {
    return JSON.stringify({type: 'GW_ERROR', payload: 'serialize_failed'});
  }
}

function hasRNBridge(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof (window as any).ReactNativeWebView?.postMessage === 'function';
}

/**
 * Service para enviar mensagens do Web (SPA) -> App (React Native).
 * - Enfileira mensagens até a bridge existir
 * - Faz stringify seguro
 * - Exponibiliza helpers de negócio (ex.: enviar token)
 */
@Injectable({providedIn: 'root'})
export class MobileBridgeService {
  private queue: string[] = [];
  private started = false;
  private flushIntervalId?: number;

  /** Opcional: chame no AppComponent para começar a drenar a fila */
  start(): void {
    if (this.started) return;
    this.started = true;

    const flush = () => {
      if (!hasRNBridge() || this.queue.length === 0) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bridge = (window as any).ReactNativeWebView;
      // drena tudo de uma vez
      while (this.queue.length) {
        const msg = this.queue.shift()!;
        bridge.postMessage(msg);
      }
    };

    // Tenta drenar frequentemente nos primeiros segundos
    this.flushIntervalId = window.setInterval(flush, 300);

    // Para de checar agressivamente após alguns segundos
    window.setTimeout(() => {
      if (this.flushIntervalId) {
        window.clearInterval(this.flushIntervalId);
        this.flushIntervalId = undefined;
      }
      // fallback: drene ao focar (quando a WebView volta ao foreground)
      window.addEventListener('focus', flush);
      // drene também em load completo
      window.addEventListener('load', flush);
      // tentativa final a cada navegação SPA (se você usa router, pode chamar flush no NavigationEnd)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') flush();
      });
    }, 6000);
  }

  /** Envia mensagem genérica */
  send<T = unknown>(message: BridgeMessage<T>): void {
    const wire = safeStringify(message);
    if (hasRNBridge()) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).ReactNativeWebView.postMessage(wire);
      return;
    }
    // ainda não existe a bridge → enfileira
    this.queue.push(wire);
  }
}
